import React, { useContext } from "react";
import {
  Field as FFField,
  FieldProps as FFFieldProps,
  FieldRenderProps as FFFieldRenderProps
} from "react-final-form";

import { AppStateContext } from "../../containers/App";
import { AnalyticsSessionContext } from "../../containers/CustomerPage";
import { has, toArray } from "../../utils/helpers";
import DDCField, { FieldProps as DDCFieldProps } from "./";

export interface FinalFormFieldProps<T extends HTMLElement>
  extends DDCFieldProps {
  display?: boolean;
  finalForm?: FFFieldProps<any, T>;
}

export default function FinalFormField<T extends HTMLInputElement>({
  type,
  name,
  // The value is destructured and not used, since the FFField
  // will deal with what value the input has.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  value,
  ...fieldProps
}: FinalFormFieldProps<T>) {
  // Final Form has to treat checkboxes and radios a special way...
  const _type = type === "checkbox" || type === "radio" ? type : undefined;
  const [appState] = useContext(AppStateContext);
  const config = appState.config;

  const analytics = useContext(AnalyticsSessionContext);
  return (
    <FFField
      type={_type}
      name={name}
      allowNull
      render={({ input, meta }: FFFieldRenderProps<any, T>) => {
        const _errors =
          meta.touched && meta.error ? toArray(meta.error) : undefined;
        if (meta.active) {
          // Touched property only goes to true one time when the field lost focus.
          // Touched should be back to false each time the field is active
          meta.touched = false;
          analytics.current.markFieldFocused(name);

          // Mark if the address was modified after using Place autocompletion
          if (
            analytics.current.form.isAddressAutoCompleted &&
            !analytics.current.form.isAddressModifiedAfterAutoCompletion &&
            (name === "streetAddress1" ||
              name === "streetAddress2" ||
              name === "streetAddress3" ||
              name === "addressLocality" ||
              // Since we can't map autocompleted place data to this field
              // we are going to disable it, since the user will always need
              // to change the region after autocomplete.
              // name === "addressRegion" ||
              name === "postalCode")
          ) {
            analytics.current.form.isAddressModifiedAfterAutoCompletion = true;
          }
        }

        if (input.value !== null) {
          analytics.current.markFieldNotEmpty(name);
        }

        if (meta.touched) {
          analytics.current.markFieldBlurred(name);
          if (meta.error) {
            analytics.current.sendError(name);
          }
        }

        if (meta.dirty) {
          switch (name) {
            case "alternateGivenName1":
              analytics.current.transliteratedField.globalGivenName = true;
              break;

            case "alternateFamilyName1":
              analytics.current.transliteratedField.globalFamilyName = true;
              break;

            case "alternateGivenName2":
              analytics.current.transliteratedField.jpKanaGivenName = true;
              break;

            case "alternateFamilyName2":
              analytics.current.transliteratedField.jpKanaFamilyName = true;
              break;
          }
        }

        if (
          name === "appsConsent" ||
          name === "emailConsent" ||
          name === "phoneConsent" ||
          name === "postConsent"
        ) {
          if (input.value !== "") {
            analytics.current.setCommunicationMean(name, input.value);
          }
        }

        return (
          <DDCField
            {...fieldProps}
            type={type}
            name={name}
            value={input.value}
            errors={_errors}
            onFocus={event => {
              input.onFocus(event as React.FocusEvent<T>);
              if (fieldProps.onFocus) {
                fieldProps.onFocus(event);
              }
            }}
            onBlur={event => {
              input.onBlur(event as React.FocusEvent<T>);
              if (fieldProps.onBlur) {
                fieldProps.onBlur(event);
              }
            }}
            onChange={(value: any) => {
              if (fieldProps.inputProps) {
                if (
                  has(config, "services.googleMaps") &&
                  input.name === "streetAddress1"
                ) {
                  analytics.current.addGoogleMapsApiUse();
                }
              }

              input.onChange(value);
            }}
          />
        );
      }}
    />
  );
}
